import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import PageHeader from '../components/PageHeader';
import AnimatedSection from '../components/AnimatedSection';
import AlumniLulusSkeletonCard from '../components/AlumniLulusSkeletonCard';
import '../components/styles.css';
import './AlumniLulusPage.css'; // Menggunakan CSS khusus untuk layout masonry alumni
import './BeritaPage.css';
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import SEO from '../components/SEO';
import { FaShareAlt } from 'react-icons/fa';

const AlumniLulusPage = () => {
  const [alumniList, setAlumniList] = useState([]);
  const [filteredAlumni, setFilteredAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('semua');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 6; // Jumlah alumni per halaman
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const skeletonHeights = ['250px', '320px', '280px', '350px', '220px', '300px'];

  // Efek untuk mengambil daftar tahun unik dari tanggal kelulusan
  useEffect(() => {
    const getYears = async () => {
      if (alumniList.length > 0) {
        const yearSet = new Set();
        alumniList.forEach(item => {
          if (item.date && item.date.toDate) {
            yearSet.add(item.date.toDate().getFullYear());
          }
        });
        // Urutkan tahun dari yang terbaru
        const sortedYears = Array.from(yearSet).sort((a, b) => b - a);
        setYears(['semua', ...sortedYears]);
      }
    };
    getYears();
  }, [alumniList]);

  // Mengambil semua data alumni lulus sekali
  useEffect(() => {
    const fetchAllAlumni = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'alumni_lulus'), orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAlumniList(data);
        setFilteredAlumni(data);
      } catch (error) {
        console.error("Error fetching alumni lulus data: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllAlumni();
  }, []);

  // Efek untuk filtering dan searching
  useEffect(() => {
    let results = alumniList;

    if (selectedYear !== 'semua') {
      results = results.filter(item => item.date?.toDate().getFullYear().toString() === selectedYear);
    }

    if (searchTerm) {
      results = results.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (item.excerpt && item.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredAlumni(results);
    setVisibleCount(itemsPerPage); // Reset ke jumlah awal setiap kali filter berubah
  }, [searchTerm, selectedYear, alumniList]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Logika Infinite Scroll Client-side
  const currentItems = filteredAlumni.slice(0, visibleCount);

  const observer = useRef();
  const lastAlumniElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleCount < filteredAlumni.length) {
        setVisibleCount(prev => prev + itemsPerPage);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, visibleCount, filteredAlumni.length]);

  const handleShare = async (e, item) => {
    e.stopPropagation(); // Mencegah lightbox terbuka saat tombol share diklik

    const shareData = {
      title: item.title,
      text: `Alumni Lulus SMK Pariwisata Sumbangsih: ${item.title}`,
      url: window.location.href, // Menggunakan URL halaman saat ini
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback untuk browser yang tidak mendukung Web Share API
      navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
      alert('Tautan dan info alumni lulus telah disalin ke clipboard!');
    }
  };

  return (
    <>
      <SEO 
        title="Alumni Lulus - SMK Pariwisata Sumbangsih" 
        description="Daftar alumni kelulusan SMK Pariwisata Sumbangsih yang telah sukses berkarier dan menempuh pendidikan lebih lanjut." 
      />

      <PageHeader 
        title="Alumni Lulus" 
        subtitle="Kumpulan data alumni sekolah kami yang telah lulus." 
      />
      <AnimatedSection>
        <section className="section">
          <div className="container">
            <div className="alumni-lulus-filter-container">
              <div className="search-bar-wrapper">
                <input
                  type="text"
                  placeholder="Cari nama atau deskripsi alumni..."
                  className="search-bar"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="year-filter">Tahun Lulus:</label>
                <div className="select-wrapper">
                  <select id="year-filter" value={selectedYear} onChange={handleYearChange}>
                    {years.map(year => (
                      <option key={year} value={year}>
                        {year === 'semua' ? 'Semua' : year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loading && filteredAlumni.length === 0 ? (
              <div className="alumni-lulus-masonry-grid">
                {Array.from({ length: itemsPerPage }).map((_, index) => (
                  <AlumniLulusSkeletonCard key={index} height={skeletonHeights[index % skeletonHeights.length]} />
                ))}
              </div>
            ) : currentItems.length > 0 ? (
              <div key={`${selectedYear}-${searchTerm}`} className={currentItems.length === 1 ? 'berita-list single-card-center' : 'alumni-lulus-masonry-grid'}>
                {currentItems.map((item, index) => (
                  <div 
                    ref={index === currentItems.length - 1 ? lastAlumniElementRef : null}
                    key={item.id} 
                    className={currentItems.length === 1 ? "berita-card" : "alumni-lulus-masonry-item prestasi-card-animated"}
                    style={{ cursor: 'pointer', animationDelay: `${(index % itemsPerPage) * 0.1}s` }} 
                    onClick={() => {
                      setImageIndex(index);
                      setLightboxOpen(true);
                    }}>
                    <div className="berita-card-image-wrapper">
                      <img src={item.image} alt={item.title} className="berita-card-image" loading="lazy" />
                    </div>
                    <div className="berita-card-content">
                      <span className="berita-card-date">Kelulusan: {new Date(item.date.toDate()).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      <h3 className="berita-card-title">{item.title}</h3>
                      <p className="berita-card-excerpt">{item.excerpt}</p>
                      <div className="prestasi-share-container">
                        <button className="btn-share" onClick={(e) => handleShare(e, item)} title="Bagikan Info Ini">
                          <FaShareAlt /> Bagikan
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center' }}>Tidak ada data alumni lulus yang cocok dengan kriteria Anda.</p>
            )}

          </div>
        </section>
      </AnimatedSection>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={imageIndex}
        slides={filteredAlumni.map(item => ({
          src: item.image,
          title: item.title,
          description: item.excerpt
        }))}
        plugins={[Captions, Zoom]}
        click={(api) => api.toggleControls()}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 5,
          click: 0,
        }}
        animation={{
          zoom: 500
        }}
      />
    </>
  );
};

export default AlumniLulusPage;
